/**
 * PageTransition - Wraps page content with a smooth fade-in animation on route change.
 * Uses CSS animation (page-enter class) for performance.
 * Respects prefers-reduced-motion via the global CSS rule.
 */
import { useLocation } from "wouter";
import { useRef, useEffect, type ReactNode } from "react";

interface PageTransitionProps {
  children: ReactNode;
}

export default function PageTransition({ children }: PageTransitionProps) {
  const [location] = useLocation();
  const containerRef = useRef<HTMLDivElement>(null);
  const prevLocation = useRef(location);

  useEffect(() => {
    if (prevLocation.current !== location && containerRef.current) {
      // Remove and re-add the animation class to trigger on route change
      containerRef.current.classList.remove("page-enter");
      // Force reflow to restart animation
      void containerRef.current.offsetWidth;
      containerRef.current.classList.add("page-enter");
      prevLocation.current = location;
    }
  }, [location]);

  return (
    <div ref={containerRef}>
      {children}
    </div>
  );
}
